import React, { useEffect, useState } from "react";
import { Nav, Card, Modal, Dropdown } from "react-bootstrap";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import { Button, Form as BsForm } from "react-bootstrap";
import { debounce } from "lodash";
import * as Yup from "yup";
import {
  addEducation,
  addFile,
  deleteEducation,
  getEducation,
  updateEducation,
} from "../../services/Admin/adminServices";
import Spinner from "../components/Common/Spinner";
import Pagination from "../components/Pagination";
import toast from "react-hot-toast";

const BASE_URL = "https://api.socialjusticejamaica.com";

const getMediaUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("blob:")) return path;
  return `${BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

const PdfCardMedia = ({ url, title, height = "200px", interactive = false }) => {
  const [useIframe, setUseIframe] = useState(false);
  const mediaUrl = getMediaUrl(url);
  const isPdfFile = typeof url === "string" && url.toLowerCase().split("?")[0].endsWith(".pdf");

  if (isPdfFile || useIframe) {
    return (
      <div style={{ height, overflow: interactive ? "auto" : "hidden", position: "relative", backgroundColor: "#ffffff", borderRadius: "8px" }}>
        <iframe
          src={interactive ? mediaUrl : `${mediaUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
          title={title || "PDF Document"}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            pointerEvents: interactive ? "auto" : "none",
          }}
        />
        {!interactive && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{ background: "rgba(0,0,0,0.01)", cursor: "pointer" }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        height,
        backgroundColor: "#f8fafc",
        overflowY: interactive ? "auto" : "hidden",
        borderRadius: "8px",
      }}
      className={`d-flex ${interactive ? "align-items-start justify-content-center" : "align-items-center justify-content-center"} position-relative`}
    >
      <img
        src={mediaUrl}
        alt={title || "Document"}
        onError={() => setUseIframe(true)}
        style={{
          width: "100%",
          height: interactive ? "auto" : "100%",
          maxHeight: interactive ? "none" : "100%",
          objectFit: interactive ? "contain" : "cover",
          display: "block",
        }}
      />
    </div>
  );
};

const Education = () => {
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("activeTab") || "image";
  });

  useEffect(() => {
    localStorage.setItem("activeTab", activeTab);
  }, [activeTab]);

  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [pdfs, setPdfs] = useState([]);

  const [search, setSearch] = useState("");
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // In-app PDF Viewer Modal State
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfModalUrl, setPdfModalUrl] = useState("");
  const [pdfModalTitle, setPdfModalTitle] = useState("");

  // Delete Confirmation Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const svg1 = (
    <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect x="0" y="0" width="24" height="24"></rect>
        <circle fill="#000000" cx="12" cy="5" r="2"></circle>
        <circle fill="#000000" cx="12" cy="12" r="2"></circle>
        <circle fill="#000000" cx="12" cy="19" r="2"></circle>
      </g>
    </svg>
  );

  const getEdu = async () => {
    try {
      setLoading(true);
      const res = await getEducation(currentPage, 10, activeTab, debouncedSearch);
      const items = res?.data?.data?.items || [];
      if (activeTab === "image") setImages(items);
      else if (activeTab === "video") setVideos(items);
      else if (activeTab === "audio") setAudios(items);
      else if (activeTab === "pdf") setPdfs(items);

      setTotalItems(res?.data?.data?.count || 0);
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to load educational content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEdu();
  }, [currentPage, debouncedSearch, activeTab]);

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearch(search);
    }, 500);
    handler();
    return () => handler.cancel();
  }, [search]);

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      setDeleting(true);
      await deleteEducation(itemToDelete._id);
      if (activeTab === "image") setImages((prev) => prev.filter((i) => i._id !== itemToDelete._id));
      else if (activeTab === "video") setVideos((prev) => prev.filter((v) => v._id !== itemToDelete._id));
      else if (activeTab === "audio") setAudios((prev) => prev.filter((a) => a._id !== itemToDelete._id));
      else if (activeTab === "pdf") setPdfs((prev) => prev.filter((p) => p._id !== itemToDelete._id));

      toast.success(`${activeTab.toUpperCase()} deleted successfully`);
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to delete item");
    } finally {
      setDeleting(false);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setPreviewFile(null);
    setThumbnail(null);
    setShowFormModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setPreviewFile(item.url);
    setThumbnail(item.thumbnail);
    setShowFormModal(true);
  };

  const openPdfViewer = (item) => {
    setPdfModalUrl(getMediaUrl(item.url));
    setPdfModalTitle(item.title || "PDF Document");
    setShowPdfModal(true);
  };

  const Schema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    url: Yup.string().required("File / URL is required"),
    thumbnail: activeTab === "video"
      ? Yup.string().required("Video cover thumbnail is required")
      : Yup.string().nullable(),
  });

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
  };

  return (
    <div>
      <h2 style={{ color: "#0AB7EA" }} className="fw-bold mb-3">Education</h2>

      {/* Tabs */}
      <Nav variant="tabs" activeKey={activeTab} className="mb-3">
        <Nav.Item>
          <Nav.Link eventKey="image" onClick={() => handleTabChange("image")}>
            Images
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="video" onClick={() => handleTabChange("video")}>
            Videos
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="audio" onClick={() => handleTabChange("audio")}>
            Audio
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="pdf" onClick={() => handleTabChange("pdf")}>
            PDF
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <div className="pb-2 d-flex flex-wrap justify-content-between gap-2 mb-3">
        <div className="flex-grow-1">
          <input
            type="text"
            style={{ borderRadius: "0.5rem", borderColor: "#0AB7EA" }}
            className="form-control w-100"
            placeholder={`Search ${activeTab}s...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <Button
            style={{ background: "#0AB7EA", border: 0, padding: "8px 16px" }}
            className="w-100 w-sm-auto"
            onClick={handleAddNew}
          >
            + Add New
          </Button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <Spinner />
      ) : (
        <div className="row">
          {/* Images Tab */}
          {activeTab === "image" &&
            images.map((img, index) => (
              <div className="col-md-3 mb-3 position-relative" key={img._id || index}>
                <Card className="position-relative shadow-lg" style={{ backgroundColor: "#f8f9fa" }}>
                  <Dropdown style={{ position: "absolute", top: "8px", right: "8px", zIndex: 10 }}>
                    <Dropdown.Toggle variant="light" style={{ padding: "4px 8px" }}>
                      {svg1}
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end">
                      <Dropdown.Item onClick={() => handleEdit(img)}>Edit</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleDeleteClick(img)}>Delete</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  <Card.Img
                    variant="top"
                    src={getMediaUrl(img.url)}
                    style={{ height: "200px", objectFit: "cover", width: "100%" }}
                  />
                  <div className="p-3">
                    <div className="fw-bold" style={{ color: "#0AB7EA", fontSize: "18px" }}>{img?.title}</div>
                    <div className="text-muted small mt-1">{img?.description}</div>
                  </div>
                </Card>
              </div>
            ))}

          {/* Videos Tab */}
          {activeTab === "video" &&
            videos.map((vid, index) => (
              <div className="col-md-3 mb-3 position-relative" key={vid._id || index}>
                <Card className="position-relative shadow-lg" style={{ backgroundColor: "#f8f9fa" }}>
                  <Dropdown style={{ position: "absolute", top: "8px", right: "8px", zIndex: 10 }}>
                    <Dropdown.Toggle variant="light" style={{ padding: "4px 8px" }}>
                      {svg1}
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end">
                      <Dropdown.Item onClick={() => handleEdit(vid)}>Edit</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleDeleteClick(vid)}>Delete</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  <video
                    className="rounded-top"
                    style={{ height: "200px", objectFit: "cover", width: "100%" }}
                    controls
                    poster={getMediaUrl(vid.thumbnail)}
                  >
                    <source src={getMediaUrl(vid.url)} type="video/mp4" />
                  </video>
                  <div className="p-3">
                    <div className="fw-bold" style={{ color: "#0AB7EA", fontSize: "18px" }}>{vid?.title}</div>
                    <div className="text-muted small mt-1">{vid?.description}</div>
                  </div>
                </Card>
              </div>
            ))}

          {/* Audio Tab */}
          {activeTab === "audio" &&
            audios.map((aud, index) => (
              <div className="col-md-3 mb-3 position-relative" key={aud._id || index}>
                <Card className="position-relative shadow-lg" style={{ backgroundColor: "#f8f9fa" }}>
                  <Dropdown style={{ position: "absolute", top: "8px", right: "8px", zIndex: 10 }}>
                    <Dropdown.Toggle variant="light" style={{ padding: "4px 8px" }}>
                      {svg1}
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end">
                      <Dropdown.Item onClick={() => handleEdit(aud)}>Edit</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleDeleteClick(aud)}>Delete</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  <div className="p-4 text-center bg-light border-bottom">
                    <i className="fa fa-file-audio fs-1" style={{ color: "#0AB7EA" }}></i>
                    <audio controls className="w-100 mt-3" src={getMediaUrl(aud.url)} />
                  </div>

                  <div className="p-3">
                    <div className="fw-bold" style={{ color: "#0AB7EA", fontSize: "18px" }}>{aud?.title}</div>
                    <div className="text-muted small mt-1">{aud?.description}</div>
                  </div>
                </Card>
              </div>
            ))}

          {/* PDF Tab */}
          {activeTab === "pdf" &&
            pdfs.map((pdf, index) => (
              <div className="col-md-3 mb-3 position-relative" key={pdf._id || index}>
                <Card
                  className="position-relative shadow-lg border-0 rounded-3 overflow-hidden cursor-pointer"
                  style={{ backgroundColor: "#ffffff" }}
                  onClick={() => openPdfViewer(pdf)}
                >
                  <Dropdown
                    style={{ position: "absolute", top: "8px", right: "8px", zIndex: 10 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Dropdown.Toggle variant="light" style={{ padding: "4px 8px" }}>
                      {svg1}
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end">
                      <Dropdown.Item onClick={() => handleEdit(pdf)}>Edit</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleDeleteClick(pdf)}>Delete</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  {/* Embedded PDF/Image Media */}
                  <PdfCardMedia url={pdf.url} title={pdf.title} height="200px" />

                  <div className="p-3">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <i className="fa fa-file-pdf text-danger fs-5"></i>
                      <div className="fw-bold text-truncate" style={{ color: "#0AB7EA", fontSize: "18px" }}>
                        {pdf?.title}
                      </div>
                    </div>
                    <div className="text-muted small text-truncate">{pdf?.description}</div>
                  </div>
                </Card>
              </div>
            ))}
        </div>
      )}

      {/* Add / Edit Form Modal */}
      <Modal
        show={showFormModal}
        onHide={() => {
          setShowFormModal(false);
          setPreviewFile(null);
          setThumbnail(null);
          setEditingItem(null);
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton style={{ borderBottom: "1px solid #e9ecef", backgroundColor: "#f8fafc" }}>
          <Modal.Title style={{ color: "#0AB7EA", fontWeight: "600", fontSize: "20px" }}>
            <i className={`fa ${activeTab === "image" ? "fa-image" : activeTab === "video" ? "fa-video" : activeTab === "audio" ? "fa-music" : "fa-file-pdf"} me-2`}></i>
            {editingItem ? "Edit" : "Add New"} {activeTab.toUpperCase()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Formik
            enableReinitialize
            initialValues={{
              educationId: editingItem?._id || "",
              type: activeTab,
              title: editingItem?.title || "",
              url: editingItem?.url || "",
              description: editingItem?.description || "",
              thumbnail: editingItem?.thumbnail || "",
            }}
            validationSchema={Schema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                const payload = {
                  ...values,
                  type: activeTab,
                };
                if (!editingItem) delete payload.educationId;

                if (editingItem) {
                  await updateEducation(payload);
                  toast.success("Updated successfully");
                } else {
                  await addEducation(payload);
                  toast.success("Added successfully");
                }
                getEdu();
                setShowFormModal(false);
                setPreviewFile(null);
                setThumbnail(null);
                setEditingItem(null);
                resetForm();
              } catch (error) {
                console.error("Save error:", error);
                toast.error(error?.response?.data?.message || "Error saving content");
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ setFieldValue, values, isSubmitting }) => (
              <FormikForm>
                {/* 1. File Upload Dropzone Box (Placed at top as originally structured) */}
                <BsForm.Group className="mb-4">
                  <BsForm.Label className="fw-semibold text-dark mb-2">
                    Upload {activeTab === "image" ? "Image" : activeTab === "video" ? "Video" : activeTab === "audio" ? "Audio" : "PDF Document"} *
                  </BsForm.Label>

                  <div
                    className="border p-4 text-center position-relative transition-all"
                    style={{
                      borderStyle: "dashed",
                      borderWidth: "2px",
                      borderColor: "#0AB7EA",
                      backgroundColor: "#f4fbfe",
                      borderRadius: "12px",
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <input
                      type="file"
                      id="eduFileInput"
                      className="d-none"
                      accept={
                        activeTab === "image"
                          ? "image/*"
                          : activeTab === "video"
                            ? "video/*"
                            : activeTab === "audio"
                              ? "audio/*"
                              : "application/pdf"
                      }
                      onChange={async (event) => {
                        const file = event.target.files[0];
                        if (file) {
                          const formData = new FormData();
                          formData.append("files", file);
                          try {
                            setUploading(true);
                            const res = await addFile(formData);
                            setUploading(false);

                            setPreviewFile(URL.createObjectURL(file));
                            if (res?.data?.data?.files?.[0]?.path) {
                              const filePath = res.data.data.files[0].path;
                              const fullUrl = filePath.startsWith("http")
                                ? filePath
                                : `${BASE_URL}${filePath.startsWith("/") ? "" : "/"}${filePath}`;
                              setFieldValue("url", fullUrl);
                              setFieldValue("type", activeTab);
                            }
                          } catch (error) {
                            console.error("Upload error:", error);
                            toast.error("File upload failed!");
                            setUploading(false);
                          } finally {
                            event.target.value = "";
                          }
                        }
                      }}
                    />

                    {!uploading ? (
                      <label htmlFor="eduFileInput" className="w-100 mb-0" style={{ cursor: "pointer" }}>
                        <div
                          className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                          style={{ width: "64px", height: "64px", backgroundColor: "rgba(10, 183, 234, 0.12)" }}
                        >
                          <i
                            className={`fa ${activeTab === "image"
                              ? "fa-cloud-arrow-up"
                              : activeTab === "video"
                                ? "fa-film"
                                : activeTab === "audio"
                                  ? "fa-file-audio"
                                  : "fa-file-pdf"
                              } fs-2`}
                            style={{ color: "#0AB7EA" }}
                          ></i>
                        </div>
                        <div className="fw-semibold fs-15 text-dark mb-1">
                          Click to browse or drag & drop {activeTab.toUpperCase()} file
                        </div>
                        <small className="text-muted d-block">
                          {activeTab === "image"
                            ? "Supports PNG, JPG, JPEG, WEBP"
                            : activeTab === "video"
                              ? "Supports MP4, WEBM, MOV"
                              : activeTab === "audio"
                                ? "Supports MP3, WAV, AAC"
                                : "Supports PDF documents"}
                        </small>
                      </label>
                    ) : (
                      <div className="py-3 text-center" style={{ color: "#0AB7EA" }}>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        <span className="fw-semibold">Uploading {activeTab.toUpperCase()} file...</span>
                      </div>
                    )}
                  </div>
                  <ErrorMessage name="url" component="div" className="text-danger mt-1 fs-13" />
                </BsForm.Group>

                {/* File Preview Container */}
                {previewFile && (
                  <div className="mb-4 p-3 rounded-3 border bg-white shadow-sm position-relative">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="badge" style={{ backgroundColor: "#0AB7EA", color: "#fff", fontSize: "12px" }}>
                        <i className="fa fa-eye me-1"></i> File Preview
                      </span>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="btn-icon rounded-circle p-0 d-flex align-items-center justify-content-center"
                        style={{ width: "24px", height: "24px", fontSize: "12px" }}
                        title="Remove file"
                        onClick={() => {
                          setPreviewFile(null);
                          setFieldValue("url", "");
                        }}
                      >
                        <i className="fa fa-times"></i>
                      </Button>
                    </div>

                    {activeTab === "image" && (
                      <img
                        src={getMediaUrl(previewFile)}
                        alt="Preview"
                        className="rounded w-100"
                        style={{ maxHeight: "220px", objectFit: "contain", backgroundColor: "#f8f9fa" }}
                      />
                    )}
                    {activeTab === "video" && (
                      <video
                        src={getMediaUrl(previewFile)}
                        controls
                        className="rounded w-100"
                        style={{ maxHeight: "220px", objectFit: "cover" }}
                      />
                    )}
                    {activeTab === "audio" && (
                      <audio controls src={getMediaUrl(previewFile)} className="w-100 mt-2" />
                    )}
                    {activeTab === "pdf" && (
                      <PdfCardMedia url={previewFile} title="PDF Preview" height="360px" interactive={true} />
                    )}
                  </div>
                )}

                {/* 2. Title Input */}
                <BsForm.Group className="mb-3">
                  <BsForm.Label className="fw-semibold text-dark">Title *</BsForm.Label>
                  <Field
                    className="form-control"
                    name="title"
                    placeholder={`Enter ${activeTab} title...`}
                    style={{ borderColor: "#0AB7EA", borderRadius: "8px", padding: "10px 14px" }}
                  />
                  <ErrorMessage name="title" component="div" className="text-danger mt-1 fs-13" />
                </BsForm.Group>

                {/* 3. Description Input */}
                <BsForm.Group className="mb-3">
                  <BsForm.Label className="fw-semibold text-dark">Description *</BsForm.Label>
                  <Field
                    as="textarea"
                    rows={3}
                    className="form-control"
                    name="description"
                    placeholder={`Enter detailed description for this ${activeTab}...`}
                    style={{ borderColor: "#0AB7EA", borderRadius: "8px", padding: "10px 14px" }}
                  />
                  <ErrorMessage name="description" component="div" className="text-danger mt-1 fs-13" />
                </BsForm.Group>

                {/* 4. Video Thumbnail Upload (Required for Video) */}
                {activeTab === "video" && (
                  <BsForm.Group className="mb-3">
                    <BsForm.Label className="fw-semibold text-dark">
                      Video Cover Thumbnail *
                    </BsForm.Label>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      style={{ borderColor: "#0AB7EA", borderRadius: "8px" }}
                      onChange={async (event) => {
                        const file = event.target.files[0];
                        if (file) {
                          const formData = new FormData();
                          formData.append("files", file);
                          try {
                            setUploadingThumbnail(true);
                            const res = await addFile(formData);
                            setUploadingThumbnail(false);
                            if (res?.data?.data?.files?.[0]?.path) {
                              const filePath = res.data.data.files[0].path;
                              const fullUrl = filePath.startsWith("http")
                                ? filePath
                                : `${BASE_URL}${filePath.startsWith("/") ? "" : "/"}${filePath}`;
                              setFieldValue("thumbnail", fullUrl);
                              setThumbnail(fullUrl);
                            }
                          } catch (error) {
                            console.error("Thumbnail upload error:", error);
                            toast.error("Thumbnail upload failed!");
                            setUploadingThumbnail(false);
                          } finally {
                            event.target.value = "";
                          }
                        }
                      }}
                    />
                    {uploadingThumbnail && (
                      <div className="py-2 text-info fw-semibold fs-13">
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Uploading video thumbnail...
                      </div>
                    )}
                    {thumbnail && !uploadingThumbnail && (
                      <div className="mt-3 position-relative d-inline-block">
                        <img
                          src={getMediaUrl(thumbnail)}
                          alt="Thumbnail Preview"
                          className="rounded border shadow-sm"
                          style={{ maxHeight: "140px", maxWidth: "240px", objectFit: "cover" }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute rounded-circle p-0 d-flex align-items-center justify-content-center shadow"
                          style={{ top: "-8px", right: "-8px", width: "22px", height: "22px", fontSize: "12px", lineHeight: "1" }}
                          title="Remove thumbnail"
                          onClick={() => {
                            setThumbnail(null);
                            setFieldValue("thumbnail", "");
                          }}
                        >
                          ×
                        </Button>
                      </div>
                    )}
                    <ErrorMessage name="thumbnail" component="div" className="text-danger mt-1 fs-13" />
                  </BsForm.Group>
                )}

                <Modal.Footer style={{ borderTop: "1px solid #e9ecef" }} className="px-0 pb-0 pt-3 mt-4">
                  <Button variant="secondary" className="px-4" onClick={() => setShowFormModal(false)} disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    style={{ backgroundColor: "#0AB7EA", borderColor: "#0AB7EA", padding: "8px 24px" }}
                    disabled={uploading || uploadingThumbnail || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {editingItem ? "Updating..." : "Saving..."}
                      </>
                    ) : (
                      editingItem ? "Update Content" : "Save Content"
                    )}
                  </Button>
                </Modal.Footer>
              </FormikForm>
            )}
          </Formik>
        </Modal.Body>
      </Modal>

      {/* In-App PDF Preview Modal */}
      <Modal
        show={showPdfModal}
        onHide={() => setShowPdfModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#0AB7EA" }}>
            <i className="fa fa-file-pdf text-danger me-2"></i>
            {pdfModalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: "80vh" }}>
          <iframe
            src={pdfModalUrl}
            title={pdfModalTitle}
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered size="md">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="text-danger fw-bold fs-5">
            <i className="fa fa-exclamation-triangle me-2"></i> Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          <p className="mb-0 text-secondary">
            Are you sure you want to delete <strong>"{itemToDelete?.title || "this item"}"</strong>? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pagination */}
      <div className="card-footer clearfix bg-transparent border-0 mt-3">
        <div className="d-flex justify-content-between align-items-center">
          <div></div>
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <Pagination pageCount={totalPages} pageValue={currentPage} setPage={setCurrentPage} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Education;
